import { Alert, AlertItem } from '@vmware-react/clarity';
import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { AuthApi } from 'vrlcm-gui-api';
import { AsyncOpState } from 'vrlcm-gui-types';
import { ErrorResponse, redirectToHomePage } from 'vrlcm-gui-utils';
import { SpinnerWithText } from '../../decorated-elements';
import { l10n } from '../i18n/i18nUtil';
import { InteractionBlocker } from '../../interaction-blocker';
import { AppLoadingSpinner } from '../app-loading-spinner';
import LoginForm, { LoginFormData } from './LoginForm';
import './Login.css';

type Props = RouteComponentProps;

const FormOverlayRenderer = (props: { size: 'sm' | 'md'; message: string }) => {
  const { size, message } = props;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', height: '66%' }}>
      <SpinnerWithText size={size}>{message}</SpinnerWithText>
    </div>
  );
};

interface MetadataState {
  fetchState: AsyncOpState;
  errorMessage?: string;
  data?: {
    vidmEnabled: boolean;
    vcfMode: boolean;
    vcfDebugMode: boolean;
  };
}

export const LoginWrapper: React.FC<Props> = props => {
  const [authState, setAuthState] = useState(AsyncOpState.Inprogress);

  useEffect(() => {
    AuthApi.getUserData()
      .then(({ loggedIn }) => {
        if (loggedIn) {
          redirectToHomePage();
        } else {
          setAuthState(AsyncOpState.Completed);
        }
      })
      .catch(() => {
        setAuthState(AsyncOpState.Failed);
      });
  }, []);

  return authState === AsyncOpState.Inprogress ? (
    <AppLoadingSpinner
      loadingText={l10n('components.withAppRoot.checkingAuthStatus')}
    />
  ) : (
    <Login {...props} />
  );
};

const Login: React.FC<Props> = (props: Props) => {
  const {
    location: { search }
  } = props;
  const queryParams = new URLSearchParams(search);
  const dest = queryParams.get('dest');
  const sessionTimeout = queryParams.get('sessionTimeout') === 'true';

  const [metadataState, setMetadataState] = useState<MetadataState>({
    fetchState: AsyncOpState.NotStarted
  });

  const [submitState, setSubmitState] = useState<{
    state: AsyncOpState;
    errorMessage?: string;
  }>({
    state: AsyncOpState.NotStarted
  });

  useEffect(() => {
    sessionStorage.setItem('newLogin', 'true');
  }, []);

  useEffect(() => {
    setMetadataState({
      fetchState: AsyncOpState.Inprogress
    });
    AuthApi.getUserData().then(({ loggedIn }) => {
      if (loggedIn) {
        redirectToHomePage(dest);
      } else {
        AuthApi.getUiLoginConfig().then(
          ({ vidmRegistered, vcfMode, vcfDebugMode }) =>
            setMetadataState({
              fetchState: AsyncOpState.Completed,
              data: { vidmEnabled: vidmRegistered, vcfMode, vcfDebugMode }
            }),
          err =>
            setMetadataState({
              fetchState: AsyncOpState.Failed,
              errorMessage: l10n('components.withAppRoot.checkingIDMError', [
                err ? err : ''
              ])
            })
        );
      }
    });
  }, [dest]);

  const handleSubmit = useCallback(
    (loginData: LoginFormData) => {
      setSubmitState({ state: AsyncOpState.Inprogress });
      AuthApi.login(loginData.username, loginData.password).then(
        () => {
          setSubmitState({ state: AsyncOpState.Completed });
          redirectToHomePage(dest);
        },
        (err: ErrorResponse<{ code: number; description: string }>) => {
          const errorMessage = err.response?.data?.description ?? '';
          setSubmitState({
            state: AsyncOpState.Failed,
            errorMessage:
              err.response?.status === 401
                ? errorMessage === 'Bad credentials'
                  ? l10n('components.withAppRoot.unknownCredentials')
                  : errorMessage
                : l10n('components.withAppRoot.loginFailedError', [
                    errorMessage
                  ])
          });
        }
      );
    },
    [dest]
  );

  const disableFormInteraction =
    metadataState.fetchState === AsyncOpState.Inprogress ||
    submitState.state === AsyncOpState.Inprogress;
  const vidmOptionEnabled =
    metadataState.fetchState === AsyncOpState.Completed &&
    metadataState.data &&
    metadataState.data.vidmEnabled;

  const overlaySpinnerMessage =
    metadataState.fetchState === AsyncOpState.Inprogress
      ? l10n('loading')
      : submitState.state === AsyncOpState.Inprogress
      ? `${l10n('loggingIn')}..`
      : '';

  const onFormDataChange = useCallback(() => {
    // clear login failed alert if any.
    setSubmitState({ state: AsyncOpState.NotStarted });
  }, []);

  return (
    <div>
      <div className="login-container">
        <div className="login">
          <div className="logo-icon">
            <img
              src={`/assets/VMware_by_Broadcom_Black.png`}
              width="170px"
              alt=""
            />
          </div>
          <section className="title">
            <h2 className="welcome">
              <span
                className="welcome-to"
                dangerouslySetInnerHTML={{
                  __html: l10n('components.withAppRoot.welcomeTo')
                }}
              ></span>
              <span
                className="product-name"
                dangerouslySetInnerHTML={{
                  __html: l10n('components.withAppRoot.VRSLCM')
                }}
              ></span>
              <span
                className="trademark"
                dangerouslySetInnerHTML={{
                  __html: l10n('components.withAppRoot.trademark')
                }}
              ></span>
            </h2>
          </section>
          <div className="login-group">
            {metadataState.fetchState === AsyncOpState.Failed && (
              <Alert
                small={true}
                type="danger"
                closable={true}
                ariaLiveMsg={metadataState.errorMessage}
                items={[<AlertItem>{metadataState.errorMessage}</AlertItem>]}
              />
            )}
            {sessionTimeout && (
              <Alert
                small={true}
                closable={true}
                ariaLiveMsg={l10n('components.withAppRoot.sessionInactivity')}
                items={[
                  <AlertItem>
                    {l10n('components.withAppRoot.sessionInactivity')}
                  </AlertItem>
                ]}
              />
            )}
            <InteractionBlocker
              disableInteraction={disableFormInteraction}
              overlayRenderer={
                disableFormInteraction && (
                  <FormOverlayRenderer
                    size={
                      metadataState.fetchState === AsyncOpState.Inprogress
                        ? 'sm'
                        : 'md'
                    }
                    message={overlaySpinnerMessage}
                  />
                )
              }
            >
              <LoginForm
                vcfMode={!!metadataState.data && metadataState.data.vcfMode}
                vcfDebugMode={
                  !!metadataState.data && metadataState.data.vcfDebugMode
                }
                onSubmit={handleSubmit}
                submitState={submitState.state}
                submitErrorMessage={submitState.errorMessage}
                vidmOptionEnabled={!!vidmOptionEnabled}
                onChange={onFormDataChange}
              />
            </InteractionBlocker>
          </div>
          <footer className="login-footer">
            {l10n('components.withAppRoot.copyrightFooter')}
          </footer>
        </div>
        <div className="splash-icon"></div>
      </div>
    </div>
  );
};

export default withRouter(LoginWrapper);
